'use client';

import useSWR from 'swr';

import { PropsWithChildren } from 'react';

import { useI18n } from '@fshr/context/i18n-context';
import { cn, getGeoIPCode } from '@fshr/shared-react';
import { defaultFetcher } from '@fshr/shared';

import Link from '../link';
import Logo from './logo';
import BevSafeText from '../bev-safe-text';
import Icon from '../icon';
import { type IconType } from '../icon/icon';
import LanguageSelect from '../language-select';
import { useOneTrust } from '@fshr/hooks';
import Button from '../button';
import { getConfig } from '@fshr/shared-react';

function Container({ children }: PropsWithChildren) {
	return (
		<div className="max-w-[690px] lg:max-w-[930px] xl:max-w-[1530px] mx-auto px-150 box-content">
			{children}
		</div>
	);
}

type FooterLink = {
	text: string;
	link: string;
	onClick?: (props?: any) => void;
	openInNewTab: boolean;
	geoIpRestrictions: string[];
};

type FooterLinksReturn = {
	linkCategories: { label: string; links: FooterLink[] }[];
	socialLinks: FooterLink[];
	legalLinks: FooterLink[];
};

export default function Footer() {
	const { togglePreferences } = useOneTrust();
	const { t, localeLink } = useI18n();
	const geoIpCode = getGeoIPCode();

	const { NEXT_PUBLIC_BFF_BASE_URL, NEXT_PUBLIC_WEBSITE_BASE_URL } = getConfig();

	const { data: footerLinks } = useSWR(
		localeLink(`${NEXT_PUBLIC_BFF_BASE_URL}/footer/`),
		defaultFetcher,
		{
			revalidateIfStale: false,
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
		}
	);

	const { linkCategories, socialLinks, legalLinks = [] }: FooterLinksReturn = footerLinks || {};
	const [link1, link2, ...restOfLinks] = legalLinks;
	const legalLinksWithOneTrust: FooterLink[] = [
		link1,
		link2,
		{
			text: t('Cookie Preferences'),
			link: '',
			onClick: togglePreferences,
			openInNewTab: false,
			geoIpRestrictions: [],
		},
		{
			text: t('Do Not Sell My Personal Information'),
			link: `${NEXT_PUBLIC_WEBSITE_BASE_URL}${localeLink('/do-not-sell-my-personal-information')}`,
			openInNewTab: false,
			geoIpRestrictions: [],
		},
		...restOfLinks,
	].filter(link => !!link);

	return (
		<footer className="@container/layout bg-neutral-1700 text-text-inverse-heading pb-450 pt-800 ">
			<Container>
				<div className="mb-1000 flex flex-col items-center">
					<Logo />
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-600 mb-[90px]">
					{linkCategories?.map(({ label: linkSectionTitle, links }) => (
						<div key={linkSectionTitle} className="flex flex-col gap-180">
							<h4 className="text-overline">
								<BevSafeText>{linkSectionTitle}</BevSafeText>
							</h4>
							<ul className="flex flex-col gap-200">
								{links
									.filter(({ geoIpRestrictions }) => !geoIpRestrictions.includes(geoIpCode))
									.map(({ text: title, link: href, openInNewTab }: FooterLink) => (
										<li className="leading-[10px]" key={title}>
											<Link
												href={href}
												intent="unstyled"
												prefetch={false}
												target={openInNewTab ? '_blank' : undefined}
												className={cn([
													'uppercase',
													'font-sans',
													'font-bold',
													'text-[12px]!',
													'tracking-[2px]!',
													'text-white',
													'text-caption3',
													'hover:text-neutral-900',
													'focus:text-neutral-900',
													'transition-colors',
												])}
											>
												{t(title)}
											</Link>
										</li>
									))}
							</ul>
						</div>
					))}
				</div>

				<div className="flex flex-col gap-200 pb-200 border-neutral-1400 md:flex-row md:border-b md:justify-between md:mb-200">
					<div className="flex flex-row gap-300 pb-200 border-b border-neutral-1400 md:border-b-0 md:pb-0">
						{(socialLinks as FooterLink[])
							?.filter(({ geoIpRestrictions }) => !geoIpRestrictions.includes(geoIpCode))
							.map(({ text: title, link: href, openInNewTab }: FooterLink) => (
								<Link
									key={title}
									href={href}
									target={openInNewTab ? '_blank' : undefined}
									prefetch={false}
									intent="unstyled"
									className={cn([
										'text-[20px]',
										'leading-none',
										'hover:text-neutral-900',
										'focus:text-neutral-900',
										'transition-colors',
									])}
								>
									<Icon icon={title as IconType} className="w-200 h-200" />
									<div className="visually-hidden">{t(title)}</div>
								</Link>
							))}
					</div>

					<div>
						<LanguageSelect className="border-t-white" />
					</div>
				</div>

				<div className="flex flex-col gap-100">
					<ul className="flex flex-row flex-wrap gap-100">
						{legalLinksWithOneTrust
							?.filter(({ geoIpRestrictions }) => !geoIpRestrictions.includes(geoIpCode))
							.map(({ text: title, link: href, openInNewTab, onClick }: FooterLink) => (
								<li
									key={title}
									className="flex flex-row items-center gap-100 before:content-[''] before:block before:bg-white before:w-040 before:h-040 before:rounded first:before:hidden"
								>
									{onClick ? (
										<Button
											key={title}
											onClick={onClick}
											intent="unstyled"
											className={cn([
												'text-[11px]',
												'text-white',
												'hover:text-neutral-900',
												'focus:text-neutral-900',
												'hover:underline',
												'hover:underline-offset-[5px]',
												'focus:underline',
												'focus:underline-offset-[5px]',
												'transition-colors',
											])}
										>
											<BevSafeText>{title}</BevSafeText>
										</Button>
									) : (
										<Link
											href={href}
											intent="unstyled"
											target={openInNewTab ? '_blank' : undefined}
											className={cn([
												'text-[11px]',
												'text-white',
												'hover:text-neutral-900',
												'focus:text-neutral-900',
												'hover:underline',
												'hover:underline-offset-[5px]',
												'focus:underline',
												'focus:underline-offset-[5px]',
												'transition-colors',
											])}
										>
											<BevSafeText>{title}</BevSafeText>
										</Link>
									)}
								</li>
							))}
					</ul>

					<p className="text-neutral-900 text-label normal-case!">
						&copy; Four Seasons Hotels Limited 1997-{new Date().getFullYear()}. All Rights Reserved.
					</p>
				</div>
			</Container>
		</footer>
	);
}
